/**
 * NL → PQL — translate an English request into a PQL statement.
 *
 * Registry: nl_to_pql (src/lib/ai/aiCapabilityRegistry.ts). Transport:
 * callFeatureLlm (graceful null on no provider — never throws into the UI).
 *
 * Honesty rules (LARGE-SETS-QUERY-AND-SMART-SETS-PRD.md §6.5 "glass box, human
 * hands"): the LLM only ever proposes statement TEXT. It is never in the
 * evaluation path, and a result is never applied to the item list without
 * passing through the same deterministic parser every hand-typed statement
 * goes through. An invalid response is retried once with the parse error fed
 * back (models fix their own near-misses reliably); if it's still invalid,
 * this returns ok:false rather than surface something that silently matches
 * nothing or the wrong thing.
 *
 * This module is NOT re-exported from src/lib/pql/index.ts — that package is
 * deliberately portable to non-browser contexts (a future Deno edge API); this
 * one calls the browser-side llmClient and workspace settings, so it stays a
 * separate, clearly-scoped submodule.
 */

import { callFeatureLlm } from '../../llmClient';
import { isAiCapabilityEnabled } from '../../ai/aiAssistSettings';
import { safeParse } from '../parser';
import { printQuery } from '../printer';
import type { PqlCatalog, PqlFieldSpec } from '../types';
import { DEFAULT_MODEL } from '../../../../supabase/functions/_shared/modelRequest.ts';

export interface NlToPqlResult {
  ok: boolean;
  /** Canonical PQL text, present when ok is true. */
  statement?: string;
  /** One-line explanation of what the query does, present when ok is true. */
  explanation?: string;
  /** Human-readable reason, present when ok is false (disabled / no provider / couldn't produce a valid query). */
  reason?: string;
}

export interface NlToPqlOptions {
  /** Injectable for tests; defaults to the real callFeatureLlm. */
  callLlm?: typeof callFeatureLlm;
  /** Injectable for tests; defaults to the real workspace capability check. */
  isEnabled?: (id: string) => boolean;
  conversationId?: string;
}

const CAPABILITY_ID = 'nl_to_pql';
const MAX_ATTEMPTS = 2;

function describeField(f: PqlFieldSpec): string {
  const typeHint =
    f.type === 'set' && f.options && f.options.length > 0
      ? ` — one of: ${f.options.slice(0, 12).map((o) => o.value).join(', ')}${f.options.length > 12 ? ', …' : ''}`
      : f.type === 'boolean'
        ? ' — true/false'
        : f.type === 'date-range'
          ? ' — a date field (accepts now(), -30d style durations)'
          : f.type === 'range'
            ? ' — numeric'
            : ' — free text';
  return `- ${statementFieldName(f.key)} (${f.label})${typeHint}`;
}

/** YYYY-MM-DD in local time — the same day arithmetic the parser uses for now() and -30d. */
function isoDate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * The name a statement must use for a field. Custom fields are stored as
 * `meta:<key>` but written `meta.<key>` — the tokenizer has no `:`. Listing
 * the stored key taught the model to emit text our own parser rejects: every
 * custom-field query failed ("Unexpected character ':'"), caught by the
 * golden-set eval on its first live run.
 */
function statementFieldName(key: string): string {
  return key.startsWith('meta:') ? `meta.${key.slice(5)}` : key;
}

function systemPrompt(catalog: PqlCatalog): string {
  const fieldLines = catalog.fields.map(describeField).join('\n');
  const userFnLine = catalog.context.currentUserId
    ? '- currentUser() is available and resolves to the person asking.'
    : '- currentUser() is NOT available in this context — never use it.';
  // The model does not know today's date. Without it, calendar phrases ("this
  // year", "since March") were resolved against the model's own guess — the
  // first live eval saw "not updated this year" become `< 2023-01-01`.
  const today = isoDate(catalog.context.now?.() ?? new Date());
  return [
    'You translate a plain-English request about a list of work items into a PQL',
    '(Priorities Query Language) statement. PQL is JQL-shaped: `field operator value`',
    'clauses joined by AND / OR / NOT, grouped with parentheses, with an optional',
    'ORDER BY clause. Operators: = != ~ !~ > >= < <=, IN (a, b), NOT IN (a, b),',
    'IS EMPTY, IS NOT EMPTY. String values need double quotes only when they contain',
    'spaces or punctuation. Dates are YYYY-MM-DD; relative durations look like -30d',
    '(days), -12w (weeks), -6M (months), -1y (years), always relative to now().',
    `Today is ${today}.`,
    '',
    'Only use these fields — never invent one:',
    fieldLines,
    '',
    userFnLine,
    '',
    'Conventions — prefer these exact forms:',
    '* Relative time: write a duration, never compute a calendar date for it.',
    '  "no activity for N days", "older than N days" → field < -Nd.',
    '  "in the last N days", "since N days ago" → field >= -Nd. "today" → field = now().',
    '  Use months (-6M) and years (-1y) when the request says months or years.',
    '* Calendar spans ("in the first half of 2025", "last November"): one range literal,',
    '  field = start..end. Numeric spans ("from 10 to 20 points") likewise: field = 10..20.',
    '* Choice fields (tags included): = and IN (a, b) match exact values — use them for',
    '  "tagged X", "is X", "X or Y". ~ is substring matching; use it only when the',
    '  request says "contains", "mentions" or similar.',
    '* Excluding several values: field NOT IN (a, b), not a chain of != clauses.',
    '* Negation (!=, NOT IN, NOT) also matches items where the field is empty. When the',
    '  request implies the field has a value ("assigned to a different person"), add',
    '  field IS NOT EMPTY.',
    '',
    'Respond with JSON only, no markdown fence, matching exactly:',
    '{"statement": "<the PQL statement>", "explanation": "<one short sentence, plain English, of what it returns>"}',
    '',
    'If the request is genuinely ambiguous, make the most reasonable interpretation',
    'and say so briefly in the explanation — never refuse and never ask a follow-up',
    'question, this is a one-shot translation.',
  ].join('\n');
}

interface LlmProposal {
  statement: string;
  explanation: string;
}

function extractJson(raw: string): LlmProposal | null {
  const trimmed = raw.trim().replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/, '');
  try {
    const parsed = JSON.parse(trimmed) as Partial<LlmProposal>;
    if (typeof parsed.statement === 'string') {
      return { statement: parsed.statement, explanation: parsed.explanation ?? '' };
    }
  } catch {
    // fall through
  }
  // Tolerate a bare statement (no JSON) — some models drop the wrapper under
  // low temperature; treat the whole trimmed reply as the statement.
  if (trimmed && !trimmed.startsWith('{')) {
    return { statement: trimmed, explanation: '' };
  }
  return null;
}

/**
 * Translate English into a validated PQL statement. Retries once against the
 * LLM with the parser's own error message when the first attempt doesn't
 * parse — never returns an unvalidated statement.
 */
export async function translateEnglishToPql(
  english: string,
  catalog: PqlCatalog,
  options: NlToPqlOptions = {},
): Promise<NlToPqlResult> {
  const callLlm = options.callLlm ?? callFeatureLlm;
  const checkEnabled = options.isEnabled ?? isAiCapabilityEnabled;

  const text = english.trim();
  if (!text) return { ok: false, reason: 'Type what you’re looking for first.' };

  if (!checkEnabled(CAPABILITY_ID)) {
    return { ok: false, reason: 'AI query authoring is turned off for this workspace.' };
  }

  const system = systemPrompt(catalog);
  const conversationId = options.conversationId ?? `nl-to-pql-${text.slice(0, 40)}`;

  let lastError: string | null = null;
  let lastAttemptedStatement: string | null = null;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    const userPrompt =
      attempt === 1
        ? `Request: ${text}`
        : `Request: ${text}\n\nYour previous attempt "${lastAttemptedStatement}" did not parse: ${lastError}\nFix it and respond again with the same JSON shape.`;

    const raw = await callLlm({
      userPrompt,
      systemPrompt: system,
      conversationId,
      model: DEFAULT_MODEL,
      maxTokens: 300,
      temperature: 0.1,
    });

    if (!raw) {
      return { ok: false, reason: 'AI is unavailable right now — no LLM provider is configured or reachable.' };
    }

    const proposal = extractJson(raw);
    if (!proposal) {
      lastError = 'Response was not valid JSON with a "statement" field.';
      lastAttemptedStatement = raw.slice(0, 120);
      continue;
    }

    const parsed = safeParse(proposal.statement, catalog);
    if (parsed.ok) {
      return {
        ok: true,
        statement: printQuery(parsed.query),
        explanation: proposal.explanation || undefined,
      };
    }
    lastError = parsed.error.message;
    lastAttemptedStatement = proposal.statement;
  }

  return {
    ok: false,
    reason: `AI couldn't produce a valid query for that (last issue: ${lastError}). Try rephrasing, or write the statement directly.`,
  };
}

/**
 * Repair helper: given text that currently fails to parse, ask the LLM to
 * produce a corrected statement that keeps the user's apparent intent. Reuses
 * the same one-retry validation discipline as translateEnglishToPql.
 */
export async function repairPqlStatement(
  invalidText: string,
  parseErrorMessage: string,
  catalog: PqlCatalog,
  options: NlToPqlOptions = {},
): Promise<NlToPqlResult> {
  const callLlm = options.callLlm ?? callFeatureLlm;
  const checkEnabled = options.isEnabled ?? isAiCapabilityEnabled;

  if (!checkEnabled(CAPABILITY_ID)) {
    return { ok: false, reason: 'AI query authoring is turned off for this workspace.' };
  }

  const system = systemPrompt(catalog);
  const conversationId = options.conversationId ?? `pql-repair-${invalidText.slice(0, 40)}`;

  const raw = await callLlm({
    userPrompt:
      `This PQL statement does not parse:\n${invalidText}\n\n` +
      `Parser error: ${parseErrorMessage}\n\n` +
      'Produce the smallest correction that preserves the apparent intent. ' +
      'Respond with the same JSON shape as always.',
    systemPrompt: system,
    conversationId,
    model: DEFAULT_MODEL,
    maxTokens: 300,
    temperature: 0.1,
  });

  if (!raw) {
    return { ok: false, reason: 'AI is unavailable right now — no LLM provider is configured or reachable.' };
  }

  const proposal = extractJson(raw);
  if (!proposal) {
    return { ok: false, reason: 'AI could not produce a fix for that statement.' };
  }

  const parsed = safeParse(proposal.statement, catalog);
  if (!parsed.ok) {
    return { ok: false, reason: `AI's suggested fix still doesn't parse (${parsed.error.message}).` };
  }

  return { ok: true, statement: printQuery(parsed.query), explanation: proposal.explanation || undefined };
}
